{ Jadelet, Modal } from @danielx/dui

Song from ../song-v2.civet
{ writePracticeMetadata } from ../lib/midi/practice-metadata.civet

{round} := Math

// LOCAL-ONLY test collection: five public-domain pieces with practice
// metadata (parts, sections, difficulty) baked in, for exercising the
// practice/game/lesson mechanics without hunting for MIDIs. Installed by
// main.civet only when served from localhost — never part of the shipped
// UI. Open with F4 or `testSongs()` in the console.

type Note = [pitch: number, dur: number]

type Piece =
  title: string
  // sharps(+)/flats(-)
  key: number
  bpm: number
  meter: [number, number]
  difficulty: number
  sections: {name: string, startMeasure: number, endMeasure: number}[]
  // melody, sequential [pitch, dur] on channel 0
  melody: Note[]
  // one bass root per measure on channel 1, held the whole measure
  bass: number[]

// -- helpers to keep the tunes legible ------------------------------------

q := (...pitches: number[]): Note[] -> pitches.map (p) -> [p, 1]
e := (...pitches: number[]): Note[] -> pitches.map (p) -> [p, 0.5]

PIECES: Piece[] := [
  {
    title: "Twinkle Twinkle (test)"
    key: 0, bpm: 100, meter: [4, 4], difficulty: 10
    sections: [
      {name: "A", startMeasure: 1, endMeasure: 4}
      {name: "B", startMeasure: 5, endMeasure: 8}
      {name: "A again", startMeasure: 9, endMeasure: 12}
    ]
    melody: [
      ...q(60, 60, 67, 67), ...q(69, 69), [67, 2]
      ...q(65, 65, 64, 64), ...q(62, 62), [60, 2]
      ...q(67, 67, 65, 65), ...q(64, 64), [62, 2]
      ...q(67, 67, 65, 65), ...q(64, 64), [62, 2]
      ...q(60, 60, 67, 67), ...q(69, 69), [67, 2]
      ...q(65, 65, 64, 64), ...q(62, 62), [60, 2]
    ]
    bass: [48, 48, 53, 48, 48, 55, 48, 55, 48, 48, 53, 48]
  }
  {
    title: "Ode to Joy (test)"
    key: 1, bpm: 120, meter: [4, 4], difficulty: 25
    sections: [
      {name: "Theme", startMeasure: 1, endMeasure: 8}
      {name: "Middle", startMeasure: 9, endMeasure: 12}
      {name: "Return", startMeasure: 13, endMeasure: 16}
    ]
    melody: [
      ...q(71, 71, 72, 74), ...q(74, 72, 71, 69), ...q(67, 67, 69, 71), [71, 1.5], [69, 0.5], [69, 2]
      ...q(71, 71, 72, 74), ...q(74, 72, 71, 69), ...q(67, 67, 69, 71), [69, 1.5], [67, 0.5], [67, 2]
      ...q(69, 69, 71, 67), [69, 1], ...e(71, 72), [71, 1], [67, 1]
      [69, 1], ...e(71, 72), [71, 1], [69, 1], ...q(67, 69), [62, 2]
      ...q(71, 71, 72, 74), ...q(74, 72, 71, 69), ...q(67, 67, 69, 71), [69, 1.5], [67, 0.5], [67, 2]
    ]
    bass: [55, 50, 55, 55, 55, 50, 55, 55, 55, 50, 50, 55, 55, 50, 55, 55]
  }
  {
    title: "Amazing Grace (test)"
    key: -1, bpm: 90, meter: [3, 4], difficulty: 30
    sections: [
      {name: "Verse", startMeasure: 1, endMeasure: 8}
      {name: "Refrain", startMeasure: 9, endMeasure: 16}
    ]
    melody: [
      [65, 2], [69, 1], [69, 2], [67, 1], [65, 2], [62, 1], [60, 3]
      [65, 2], [69, 1], [69, 2], [67, 1], [72, 3], [72, 3]
      [72, 2], [69, 1], [65, 2], [62, 1], [60, 3], [60, 2], [65, 1]
      [65, 2], [69, 1], [69, 2], [67, 1], [65, 3], [65, 3]
    ]
    bass: [53, 53, 46, 53, 53, 53, 48, 48, 53, 53, 46, 53, 53, 48, 53, 53]
  }
  {
    title: "Minuet in G (test)"
    key: 1, bpm: 116, meter: [3, 4], difficulty: 45
    sections: [
      {name: "A", startMeasure: 1, endMeasure: 8}
      {name: "B", startMeasure: 9, endMeasure: 16}
    ]
    melody: [
      [74, 1], ...e(67, 69, 71, 72), [74, 1], ...q(67, 67)
      [76, 1], ...e(72, 74, 76, 78), [79, 1], ...q(67, 67)
      [72, 1], ...e(74, 72, 71, 69), [71, 1], ...e(72, 71, 69, 67)
      [66, 1], ...e(67, 69, 71, 67), [69, 3]
      [74, 1], ...e(67, 69, 71, 72), [74, 1], ...q(67, 67)
      [76, 1], ...e(72, 74, 76, 78), [79, 1], ...q(67, 67)
      [72, 1], ...e(74, 72, 71, 69), [71, 1], ...e(72, 71, 69, 67)
      [66, 1], ...e(67, 69, 71, 67), [67, 3]
    ]
    bass: [55, 59, 60, 59, 57, 55, 50, 50, 55, 59, 60, 59, 57, 50, 55, 55]
  }
  {
    title: "Für Elise (test)"
    key: 0, bpm: 110, meter: [3, 4], difficulty: 60
    sections: [
      {name: "Theme", startMeasure: 1, endMeasure: 4}
      {name: "Answer", startMeasure: 5, endMeasure: 8}
    ]
    melody: [
      ...e(76, 75, 76, 75, 76, 71), ...e(74, 72), [69, 2]
      ...e(60, 64, 69), [71, 1.5], ...e(64, 68, 71), [72, 1.5]
      ...e(76, 75, 76, 75, 76, 71), ...e(74, 72), [69, 2]
      ...e(60, 64, 69), [71, 1.5], ...e(64, 72, 71), [69, 1.5]
    ]
    bass: [45, 45, 45, 52, 45, 45, 45, 45]
  }
]

Template := Jadelet.exec """
  section.song-picker.test-songs
    h2 🧪 Test Songs (local only)
    button.close(click=@close) X
    p Five pieces with practice metadata baked in — parts, sections, difficulty — for trying the learn and game mechanics.
    table
      thead
        tr
          th Title
          th Key
          th Meter
          th Difficulty
      tbody
        @items
"""

RowTemplate := Jadelet.exec """
  tr(@click)
    td @title
    td @key
    td @meter
    td @difficulty
"""

KEY_NAMES: Record<number, string> := {"-1": "F", 0: "C / Am", 1: "G"}

export TestSongs := (player: Player) ->
  loadPiece := (piece: Piece) ->
    await player.confirmUnsaved()
    Modal.hide()

    beatsPerMeasure := piece.meter[0] * 4 / piece.meter[1]

    //@ts-expect-error same untyped init main.civet uses
    player.song.init Song().toMidi()
    s := player.song
    s.title piece.title
    s.beatLength piece.bass.length * beatsPerMeasure
    s.setTempoAtBeat 0, round 60000000 / piece.bpm
    s.setTimeSignatureAtBeat 0, piece.meter[0], piece.meter[1]
    s.setKeySignatureAtBeat 0, piece.key, 0

    beat .= 0
    for [pitch, dur] of piece.melody
      s.addNote 0, pitch, 0x60, beat, dur
      beat += dur
    piece.bass.forEach (root, m) ->
      s.addNote 1, root, 0x50, m * beatsPerMeasure, beatsPerMeasure

    writePracticeMetadata s, {
      version: 2
      title: piece.title
      difficulty: piece.difficulty
      parts: [{name: "You", channels: [0]}]
      sections: piece.sections
    }

    player.trigger "reset"
    player.pushState player.getState()
    player.currentBeat 0

  self :=
    close: -> Modal.hide()
    items: ->
      PIECES.map (piece) ->
        RowTemplate
          title: piece.title
          key: KEY_NAMES[piece.key] ?? String piece.key
          meter: `${piece.meter[0]}/${piece.meter[1]}`
          difficulty: `${piece.difficulty} / 100`
          click: -> loadPiece piece

    show: ->
      Modal.show self.element

    element: null as! HTMLElement

  self.element = Template self

  player.addHotkey "f4", (-> self.show()),
    category: "Dev"
    name: "Test songs (local)"

  //@ts-expect-error console convenience
  globalThis.testSongs = self.show

  return self

export default TestSongs
